import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Theme,
  Typography,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { makeStyles, createStyles } from '@material-ui/styles';
import { DateTime } from 'luxon';
import { difference, min } from 'lodash';
import { useCampfireQuery } from '../../../global/network/useCampfireQuery';
import { AvailabilityHeader } from '../AvailabilityHeader';
import { SearchField } from '../../inputs/SearchField';
import { AlertCard, getAlertCardColors } from '../../cards/alert-card/AlertCard';
import { SessionSelect } from '../SessionSelect';
import { DateAndFrequencySelect } from '../DateSelect';
import { DateSelect } from '../DateSelect/DateSelect';
import { CustomFrequencyDialog } from '../DateSelect/CustomFrequencyDialog';
import { CustomFrequencyValue, DEFAULT_CUSTOM_FREQUENCY } from '../DateSelect/consts';
import { describeCustomFrequency, filterByCustomFrequency } from '../DateSelect/helper';
import { ACTIVITY_AVAILABILITY_ACTIVITY_ENROLMENT } from '../ActivityAvailability/activity-availability-activity-enrolment-model.gql';
import {
  ActivityAvailabilityGetActivityEnrolment,
  ActivityAvailabilityGetActivityEnrolmentVariables,
} from '../ActivityAvailability/__generated__/ActivityAvailabilityGetActivityEnrolment';
import { ACTIVITY_AVAILABILITY_ACTIVITY } from '../ActivityAvailability/activity-availability-activity-model.gql';
import {
  ActivityAvailabilityGetActivity,
  ActivityAvailabilityGetActivityVariables,
  ActivityAvailabilityGetActivity_vm_activity as ActivityType,
} from '../ActivityAvailability/__generated__/ActivityAvailabilityGetActivity';
import { useCampfireFetch } from '../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../global/config/useApiUrl';
import { useSnackbar } from '../../../global/config/useSnackbar';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actionContainer: {
      paddingTop: '20px',
      paddingBottom: '30px',
      paddingRight: '22px',
    },
    toolbarButton: {
      borderRadius: '8px',
    },
    toolbarButtonSelected: {
      backgroundColor: theme.color.grey.neutral200,
      color: 'white',
      '&:hover': {
        opacity: 0.8,
        backgroundColor: theme.color.grey.neutral200,
      },
    },
    actionButton: {
      textTransform: 'capitalize',
    },
  })
);

interface BulkRosteringProps {
  activityId: string;
  selectedDate?: string;
  open: boolean;
  onClose: () => void;
  volunteerId: string;
}

enum AvailaibiltyStepEnum {
  SELECT_SESSIONS,
  SELECT_DATES,
}

type DateMode = 'bulk' | 'range';

export function BulkRostering({ activityId, onClose, open, selectedDate, volunteerId }: BulkRosteringProps) {
  const { data: enrolmentData } = useCampfireQuery<
    ActivityAvailabilityGetActivityEnrolment,
    ActivityAvailabilityGetActivityEnrolmentVariables
  >(ACTIVITY_AVAILABILITY_ACTIVITY_ENROLMENT, {
    options: {
      variables: {
        volunteerId,
        activityId,
      },
    },
  });

  const { data: activityData } = useCampfireQuery<
    ActivityAvailabilityGetActivity,
    ActivityAvailabilityGetActivityVariables
  >(ACTIVITY_AVAILABILITY_ACTIVITY, {
    options: {
      variables: {
        activityId,
        from: DateTime.local().toISODate(),
        until: DateTime.local()
          .plus({ years: 1 })
          .toISODate(),
      },
    },
  });

  const activity = activityData?.vm.activity || ({} as ActivityType);

  const [step, setStep] = React.useState(AvailaibiltyStepEnum.SELECT_SESSIONS);
  const [searchValue, setSearchValue] = React.useState('');
  const [dateMode, setDateMode] = React.useState<DateMode>('bulk');
  const [rangeStartDate, setRangeStartDate] = React.useState<Date | null>(null);
  const [rangeEndDate, setRangeEndDate] = React.useState<Date | null>(null);
  const handleCancel = () => {
    onClose();
  };
  const [value, setValue] = React.useState({
    sessionIds: [] as string[],
    dates: [selectedDate] as string[],
  });

  const filterSessions =
    activity.sessions?.filter((session) => session.name.toLowerCase().includes(searchValue.toLowerCase())) || [];

  const onSelectSession = (selectedSessionIds: string[]) => {
    setValue({
      ...value,
      sessionIds: selectedSessionIds,
    });
  };

  const onSelectDates = (selectedDates: string[]) => {
    setValue({
      ...value,
      dates: selectedDates,
    });
  };

  const [customFrequency, setCustomFrequency] = React.useState<CustomFrequencyValue | null>(null);
  const [showCustomFrequencyDialog, setShowCustomFrequencyDialog] = React.useState(false);

  const handleDateModeChange = (newMode: DateMode | null) => {
    if (newMode !== null) {
      setDateMode(newMode);
      setValue({ ...value, dates: newMode === 'range' ? [] : ([selectedDate] as string[]) });
      setRangeStartDate(null);
      setRangeEndDate(null);
    }
  };

  const recurrences = React.useMemo(() => {
    switch (activity.__typename) {
      case 'VOLUNTEER_RecurringActivityType':
        return activity.schedule.recurrences;
      default:
        return [];
    }
  }, [activity]);

  const classes = useStyles();
  const { lightTextColor } = getAlertCardColors('info');

  const { unavailabilities } = enrolmentData?.vm.volunteer?.activityEnrolment || {};
  const onBack = () => setStep(Math.max(step - 1, 0));
  const isNextEnabled = (() => {
    if (step === AvailaibiltyStepEnum.SELECT_SESSIONS) {
      return Boolean(value.sessionIds?.length);
    }
    return Boolean(value.dates?.length);
  })();

  const handleNext = () => {
    if (step === AvailaibiltyStepEnum.SELECT_DATES) {
      handleSubmit();
    } else {
      setStep(Math.min(step + 1, AvailaibiltyStepEnum.SELECT_DATES));
    }
  };

  const dateUnavailabilities = unavailabilities?.map((unavailability) => unavailability.activityDate) || [];
  const occurrences = activity.occurrencesBetween || [];
  const selectableOccurrences = difference(occurrences, dateUnavailabilities);

  const generateDatesInRange = (start: Date, end: Date): string[] => {
    const dates: string[] = [];
    const startDt = DateTime.fromJSDate(start).startOf('day');
    const endDt = DateTime.fromJSDate(end).startOf('day');

    for (let cursor = startDt; cursor <= endDt; cursor = cursor.plus({ days: 1 })) {
      const dateStr = cursor.toFormat('yyyy-MM-dd');
      // Only include dates that ARE occurrences (opposite of RosterTemplateForm)
      if (selectableOccurrences.includes(dateStr)) {
        dates.push(dateStr);
      }
    }
    return dates;
  };

  const updateRangeRosterDates = (start: Date | null, end: Date | null) => {
    if (start && end && start <= end) {
      const datesInRange = generateDatesInRange(start, end);
      setValue({ ...value, dates: datesInRange });
    } else {
      setValue({ ...value, dates: [] });
    }
  };

  const handleRangeStartChange = (date: Date | undefined) => {
    const newStart = date ?? null;
    setRangeStartDate(newStart);
    updateRangeRosterDates(newStart, rangeEndDate);
  };

  const handleRangeEndChange = (date: Date | undefined) => {
    const newEnd = date ?? null;
    setRangeEndDate(newEnd);
    updateRangeRosterDates(rangeStartDate, newEnd);
  };

  const campfireMutation = useCampfireFetch({ defer: true });
  const apiUrl = useApiUrl();

  const { setSnackbar } = useSnackbar();

  const handleSubmit = () => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/activity/enrolment/attendance/add`,
        method: 'post',
        data: {
          activityId: activityId,
          activityEnrolmentId: enrolmentData?.vm.volunteer?.activityEnrolment?.activityEnrolmentId,
          activityDates: value.dates,
          activityRoleIds: [],
          sessionIds: value.sessionIds,
          volunteerId: volunteerId,
        },
      })
      .then((response) => {
        if (response.status === 500) {
          setSnackbar({
            variant: 'error',
            open: true,
            message: 'Bulk rostering failed',
          });
        } else {
          setSnackbar({
            variant: 'success',
            open: true,
            message: 'Set rosterings successfully',
          });
        }

        // add warning for when some sessions are full

        onClose();
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md' style={{ zIndex: 2000 }}>
      <DialogTitle>
        <AvailabilityHeader
          header='Bulk Rostering'
          subHeader={step === AvailaibiltyStepEnum.SELECT_SESSIONS ? 'Select Sessions/s' : 'Select Dates'}
        >
          <Box display={'flex'} alignItems='center'>
            {step === AvailaibiltyStepEnum.SELECT_SESSIONS && (
              <SearchField
                value={searchValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  setSearchValue(e.target.value)
                }
              />
            )}
          </Box>
        </AvailabilityHeader>
      </DialogTitle>
      <DialogContent>
        <Box>
          {(() => {
            switch (step) {
              case AvailaibiltyStepEnum.SELECT_SESSIONS:
                return (
                  <SessionSelect
                    selectedSessionIds={value.sessionIds || []}
                    sessions={filterSessions}
                    onSelectSession={onSelectSession}
                  />
                );
              case AvailaibiltyStepEnum.SELECT_DATES:
                return (
                  <Box>
                    <Box display='flex' alignItems='center' mb={2} style={{ gap: '12px' }}>
                      <Typography variant='body2' style={{ fontWeight: 600 }}>
                        Mode
                      </Typography>
                      <ToggleButtonGroup
                        value={dateMode}
                        exclusive
                        onChange={(_unusedEvent, newMode) => handleDateModeChange(newMode)}
                        size='small'
                      >
                        <ToggleButton
                          value='bulk'
                          style={
                            dateMode === 'bulk'
                              ? { backgroundColor: '#7047EB', color: '#fff', padding: '6px 12px' }
                              : { padding: '6px 12px' }
                          }
                        >
                          <Typography style={{ fontWeight: 600, fontSize: '12px' }}>Bulk</Typography>
                        </ToggleButton>
                        <ToggleButton
                          value='range'
                          style={
                            dateMode === 'range'
                              ? { backgroundColor: '#7047EB', color: '#fff', padding: '6px 12px' }
                              : { padding: '6px 12px' }
                          }
                        >
                          <Typography style={{ fontWeight: 600, fontSize: '12px' }}>Range</Typography>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {dateMode === 'bulk' ? (
                      recurrences.length > 0 ? (
                        <DateAndFrequencySelect
                          occurrences={selectableOccurrences}
                          recurrences={recurrences}
                          value={value.dates || []}
                          onChange={onSelectDates}
                          key={activity.activityId}
                          availabilities={[]}
                          unavailabilities={dateUnavailabilities}
                          startDate={min([activity.startDate, selectableOccurrences[0]])}
                          allowCustomFrequency
                        />
                      ) : (
                        <Box>
                          <Box display='flex' alignItems='center' marginBottom={1} style={{ gap: 8 }}>
                            {customFrequency && (
                              <Typography style={{ fontSize: 13 }}>
                                {describeCustomFrequency(customFrequency)}
                              </Typography>
                            )}
                            <Button
                              size='small'
                              color='primary'
                              onClick={() => setShowCustomFrequencyDialog(true)}
                              style={{ textTransform: 'none', minWidth: 0 }}
                            >
                              {customFrequency ? 'Edit' : 'Custom frequency…'}
                            </Button>
                          </Box>
                          <CustomFrequencyDialog
                            open={showCustomFrequencyDialog}
                            onClose={() => setShowCustomFrequencyDialog(false)}
                            value={customFrequency || DEFAULT_CUSTOM_FREQUENCY}
                            onChange={(newCustomFrequency) => {
                              setCustomFrequency(newCustomFrequency);
                              setShowCustomFrequencyDialog(false);
                              onSelectDates(selectableOccurrences.filter(filterByCustomFrequency(newCustomFrequency)));
                            }}
                          />
                          <DateSelect
                            multiple
                            value={value.dates || []}
                            occurrences={selectableOccurrences}
                            availabilities={[]}
                            unavailabilities={dateUnavailabilities}
                            startDate={min([activity.startDate, selectableOccurrences[0]])}
                            onChange={onSelectDates}
                          />
                        </Box>
                      )
                    ) : (
                      <Box>
                        <Box mb={2}>
                          <AlertCard variant='info'>
                            <Typography style={{ fontSize: '14px', fontWeight: 400, color: lightTextColor }}>
                              Rostering will only occur on days when the activity runs within your selected range.
                            </Typography>
                          </AlertCard>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <KeyboardDatePicker
                              fullWidth
                              variant='inline'
                              format='dd/MM/yyyy'
                              id='range-start-date'
                              value={rangeStartDate}
                              label='Start Date'
                              inputVariant='outlined'
                              onChange={(date) => handleRangeStartChange(date?.toJSDate())}
                              autoOk
                              placeholder='dd/mm/yyyy'
                              allowKeyboardControl
                              InputAdornmentProps={{ position: 'start' }}
                              KeyboardButtonProps={{ tabIndex: -1 }}
                              minDate={activity.startDate}
                              PopoverProps={{ style: { zIndex: 2100 } }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <KeyboardDatePicker
                              fullWidth
                              variant='inline'
                              format='dd/MM/yyyy'
                              id='range-end-date'
                              value={rangeEndDate}
                              label='End Date'
                              inputVariant='outlined'
                              onChange={(date) => handleRangeEndChange(date?.toJSDate())}
                              autoOk
                              placeholder='dd/mm/yyyy'
                              allowKeyboardControl
                              InputAdornmentProps={{ position: 'start' }}
                              KeyboardButtonProps={{ tabIndex: -1 }}
                              minDate={rangeStartDate ?? activity.startDate}
                              PopoverProps={{ style: { zIndex: 2100 } }}
                            />
                          </Grid>
                        </Grid>
                        <Box display='flex' justifyContent='flex-end' alignItems='center' style={{ marginTop: 12 }}>
                          <Typography variant='caption' style={{ fontWeight: 700 }}>
                            {value.dates?.length || 0} date{(value.dates?.length || 0) !== 1 ? 's' : ''} to roster
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              default:
                return null;
            }
          })()}
        </Box>
      </DialogContent>
      <DialogActions className={classes.actionContainer}>
        {step === AvailaibiltyStepEnum.SELECT_SESSIONS ? (
          <Button onClick={handleCancel} disableElevation className={classes.actionButton}>
            Cancel
          </Button>
        ) : (
          <Button onClick={onBack} disableElevation className={classes.actionButton}>
            Back
          </Button>
        )}
        <Button
          onClick={handleNext}
          disableElevation
          variant='contained'
          color='primary'
          disabled={!isNextEnabled}
          className={classes.actionButton}
        >
          {step === AvailaibiltyStepEnum.SELECT_DATES ? 'Submit' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
